import React, { useState, useEffect } from 'react';
import PhaseProgressPipeline from './PhaseProgressPipeline';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Select,
  FormControl,
  Chip,
  useMediaQuery,
  useTheme,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ExitToApp,
  AdminPanelSettings,
  FolderOpen,
  Home,
  Build,
  Explore,
  Rocket,
  CloudUpload,
  DirectionsRun,
  Description,
  MenuBook as MenuBookIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserProjects } from '../utils/api';

const phases = [
  {
    name: 'PREPARE',
    path: 'prepare',
    color: '#F5821F',
    icon: <Build />,
    subphases: [
      // { name: 'Technical Requirements', path: 'technical-requirements' },
      // { name: 'Integration Map', path: 'integration-map' },
      { name: 'Workshop Schedule', path: 'workshop-schedule' },
      { name: 'RACI Matrix', path: 'raci-matrix' },
      { name: 'Project Charter', path: 'templates' },
    ],
  },
  {
    name: 'EXPLORE',
    path: 'explore',
    color: '#4DB6AC',
    icon: <Explore />,
    subphases: [
      { name: 'Fit-to-Standard Presentation', path: 'fit-to-standard' },
      // { name: 'Current State Analysis', path: 'current-state', customLink: null },
      // { name: 'Future State Design', path: 'future-state', customLink: null },
      { name: 'Gap Analysis', path: 'gap-analysis', customLink: null },
      { name: 'BPML & BPD', path: 'bpml-bpd' },
      // { name: 'Process Mapping', path: 'process-mapping', customLink: null },
      // { name: 'Solution Design', path: 'solution-design', customLink: (projectId) => `/projects/${projectId}/solution-proposal` },
    ],
  },
  {
    name: 'REALIZE',
    path: 'realize',
    color: '#9C27B0',
    icon: <Build />,
    subphases: [
      { name: 'RICEFW List', path: 'ricefw-list', customLink: (projectId) => `/projects/${projectId}/realize/ricefw-list` },
      // { name: 'Configuration', path: 'configuration', customLink: null },
      // { name: 'Development', path: 'development', customLink: null },
      {
        name: 'Test Scripts',
        path: 'unit-testing',
        customLink: (projectId) => `/projects/${projectId}/realize/uat-test-scripts`,
      },
      // { name: 'Data Migration', path: 'data-migration', customLink: null },
    ],
  },
  {
    name: 'DEPLOY',
    path: 'deploy',
    color: '#FF5722',
    icon: <CloudUpload />,
    subphases: [
      { name: 'Training', path: 'training' },
      { name: 'Cutover Planning', path: 'cutover' },
      { name: 'Go-Live', path: 'go-live' },
    ],
  },
  {
    name: 'RUN',
    path: 'run',
    color: '#00BCD4',
    icon: <DirectionsRun />,
    subphases: [
      { name: 'Hypercare Support', path: 'hypercare' },
      // { name: 'Performance Monitoring', path: 'performance' },
      // { name: 'Continuous Improvement', path: 'improvement' },
      { name: 'Knowledge Transfer', path: 'knowledge-transfer' },
      { name: 'Application Support', path: 'operations' },
    ],
  },
];

const NavigationBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const { currentUser: user, logout } = useAuth();
  const theme = useTheme();

  // Responsive breakpoints for different screen sizes
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Mobile: < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // Tablet: 600-900px
  const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Laptop: 900-1200px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // Desktop: >= 1200px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // Mobile + Tablet

  const [phaseMenus, setPhaseMenus] = useState({});
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileExpandedPhase, setMobileExpandedPhase] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Initialize selected project from localStorage
  useEffect(() => {
    const loadSelectedProject = () => {
      try {
        const savedProject = localStorage.getItem('selectedProject');
        console.log('[NavigationBar] Loading selected project from localStorage:', savedProject);
        if (savedProject) {
          const project = JSON.parse(savedProject);
          setSelectedProject(project);
          console.log('[NavigationBar] Selected project loaded:', project);
        } else {
          setSelectedProject(null);
          console.log('[NavigationBar] No selected project in localStorage');
        }
      } catch (error) {
        console.error('[NavigationBar] Error parsing saved project:', error);
        localStorage.removeItem('selectedProject');
        setSelectedProject(null);
      }
    };

    // Load initially
    loadSelectedProject();

    // Listen for changes to localStorage (from other components)
    const handleStorageChange = (e) => {
      if (e.key === 'selectedProject') {
        console.log('[NavigationBar] localStorage changed event received');
        loadSelectedProject();
      }
    };

    // Custom event for same-window updates
    const handleProjectChangeEvent = () => {
      console.log('[NavigationBar] projectChanged event received');
      loadSelectedProject();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('projectChanged', handleProjectChangeEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('projectChanged', handleProjectChangeEvent);
    };
  }, []);

  // Load projects when user is available
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadProjects = async () => {
    try {
      if (!user?.id) {
        console.log('No user ID available');
        return;
      }

      console.log('Loading projects for user:', user.id);
      const response = await getUserProjects(user.id);
      console.log('Projects response:', response);

      if (response.success && response.projects) {
        setProjects(response.projects);
        console.log('Projects loaded:', response.projects);
      } else {
        console.warn('Failed to load projects:', response.message);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const handlePhaseMenuOpen = (event, phaseName) => {
    setPhaseMenus({ ...phaseMenus, [phaseName]: event.currentTarget });
  };

  const handlePhaseMenuClose = (phaseName) => {
    setPhaseMenus({ ...phaseMenus, [phaseName]: null });
  };

  const handleSubphaseClick = (phase, subphase) => {
    if (!selectedProject) {
      // No project selected, redirect to settings
      console.warn('No project selected, redirecting to settings');
      navigate('/settings');
      handlePhaseMenuClose(phase.name);
      setMobileDrawerOpen(false);
      return;
    }

    // Check if subphase has a custom link
    const targetPath = subphase.customLink
      ? subphase.customLink(selectedProject.id)
      : `/projects/${selectedProject.id}/${phase.path}/${subphase.path}`;

    console.log('Navigating to:', targetPath, 'for project:', selectedProject.id);
    navigate(targetPath);
    handlePhaseMenuClose(phase.name);
    setMobileDrawerOpen(false);
  };

  const handleProjectChange = (event) => {
    const value = event.target.value;
    const project = projects.find(
      (p) => String(p.id) === String(value)
    );
    setSelectedProject(project);
    localStorage.setItem('selectedProject', JSON.stringify(project));

    // Dispatch custom event to notify other components (HomePage dialog, etc.)
    window.dispatchEvent(new Event('projectChanged'));

    navigate('/home');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobilePhase = (phaseName) => {
    setMobileExpandedPhase(mobileExpandedPhase === phaseName ? null : phaseName);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'Administrator';

  // Desktop Phase Navigation (Center) - Responsive sizing
  const renderDesktopPhaseNav = () => (
    <Box sx={{
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      gap: 0.5,
      justifyContent: 'center',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    }}>
      {selectedProject && phases.map((phase) => (
        <React.Fragment key={phase.name}>
          <Button
            color="inherit"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => handlePhaseMenuOpen(e, phase.name)}
            disabled={phase.disabled}
            sx={{
              color: location.pathname.includes(`/${phase.path}`) ? phase.color : 'inherit',
              fontWeight: location.pathname.includes(`/${phase.path}`) ? 600 : 400,
              fontSize: { md: '0.75rem', lg: '0.78rem', xl: '0.875rem' },
              px: { md: 0.5, lg: 0.75, xl: 1.5 },
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              '&:hover': {
                color: phase.color,
              },
            }}
          >
            {phase.name}
          </Button>
          {!phase.disabled && (
            <Menu
              anchorEl={phaseMenus[phase.name]}
              open={Boolean(phaseMenus[phase.name])}
              onClose={() => handlePhaseMenuClose(phase.name)}
              PaperProps={{
                sx: {
                  bgcolor: 'background.paper',
                  minWidth: 220,
                  borderTop: `3px solid ${phase.color}`,
                },
              }}
            >
              {phase.subphases.map((subphase) => (
                <MenuItem
                  key={subphase.path}
                  onClick={() => handleSubphaseClick(phase, subphase)}
                  sx={{
                    '&:hover': {
                      bgcolor: `${phase.color}20`,
                    },
                  }}
                >
                  {subphase.name}
                </MenuItem>
              ))}
            </Menu>
          )}  
        </React.Fragment>
      ))}
    </Box>
  );

  // Mobile Navigation Drawer
  const renderMobileNav = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      PaperProps={{
        sx: { width: 280, bgcolor: 'background.paper' },
      }}
    >
      <List>
        <ListItem button onClick={() => { navigate('/home'); setMobileDrawerOpen(false); }}>
          <ListItemIcon><Home /></ListItemIcon>
          <ListItemText primary="Home" />
        </ListItem>

        <ListItem button onClick={() => { navigate('/settings'); setMobileDrawerOpen(false); }}>
          <ListItemIcon><SettingsIcon /></ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>

        <Divider sx={{ my: 1 }} />

        {selectedProject && (
          <ListItem button onClick={() => { navigate(`/projects/${selectedProject.id}/overview`); setMobileDrawerOpen(false); }}>
            <ListItemIcon><Description /></ListItemIcon>
            <ListItemText primary="Project Overview" />
          </ListItem>
        )}

        {isAdmin && (
          <ListItem button onClick={() => { navigate('/admin'); setMobileDrawerOpen(false); }}>
            <ListItemIcon><AdminPanelSettings /></ListItemIcon>
            <ListItemText primary="Admin" />
          </ListItem>
        )}

        <ListItem button onClick={() => { navigate('/documentation'); setMobileDrawerOpen(false); }}>
          <ListItemIcon><MenuBookIcon /></ListItemIcon>
          <ListItemText primary="Selectivate Guide" />
        </ListItem>

        {selectedProject && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                SAP PHASES
              </Typography>
            </Box>
            {phases.map((phase) => (
              <React.Fragment key={phase.name}>
                <ListItem
                  button
                  disabled={phase.disabled}
                  onClick={() => !phase.disabled && toggleMobilePhase(phase.name)}
                  sx={{
                    bgcolor: location.pathname.includes(`/${phase.path}`) ? `${phase.color}20` : 'transparent',
                    '&.Mui-disabled': { opacity: 0.6 }
                  }}
                >
                  <ListItemIcon sx={{ color: phase.color }}>
                    {phase.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={phase.name}
                    primaryTypographyProps={{ fontWeight: 600, color: phase.color }}
                  />
                  {!phase.disabled && (mobileExpandedPhase === phase.name ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItem>
                <Collapse in={!phase.disabled && mobileExpandedPhase === phase.name} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {phase.subphases.map((subphase) => (
                      <ListItem
                        button
                        key={subphase.path}
                        onClick={() => handleSubphaseClick(phase, subphase)}
                        sx={{ pl: 6 }}
                      >
                        <ListItemText
                          primary={subphase.name}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: scrolled ? 'rgba(18, 18, 18, 0.95)' : 'background.paper',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? 1 : 0,
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          boxShadow: scrolled ? 2 : 0,
        }}
      >
        <Toolbar sx={{ gap: isLaptop ? 1 : 2, px: { xs: 1, sm: 2, md: 2, lg: 3 } }}>
          {/* Left Section: Mobile Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isLaptop ? 1 : 2, flexShrink: 0 }}>
            {isSmallScreen && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ mr: 0 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Home/Admin/Projects buttons - On left side */}
            {!isSmallScreen && (
              <>
                <Button
                  color="inherit"
                  startIcon={<Home />}
                  onClick={() => navigate('/home')}
                  sx={{
                    color: location.pathname === '/home' ? theme.palette.primary.main : 'inherit',
                    fontWeight: location.pathname === '/home' ? 600 : 400,
                    fontSize: isLaptop ? '0.85rem' : '0.875rem',
                    px: isLaptop ? 1 : 1.5,
                  }}
                >
                  Home
                </Button>

                {selectedProject && (
                  <Button
                    color="inherit"
                    startIcon={<Description />}
                    onClick={() => navigate(`/projects/${selectedProject.id}/overview`)}
                    sx={{
                      color: location.pathname.includes('/overview') ? theme.palette.primary.main : 'inherit',
                      fontWeight: location.pathname.includes('/overview') ? 600 : 400,
                      fontSize: isLaptop ? '0.85rem' : '0.875rem',
                      px: isLaptop ? 1 : 1.5,
                    }}
                  >
                    Overview
                  </Button>
                )}

                {isAdmin && (
                  <Button
                    color="inherit"
                    startIcon={<AdminPanelSettings />}
                    onClick={() => navigate('/admin')}
                    sx={{
                      color: location.pathname === '/admin' ? theme.palette.primary.main : 'inherit',
                      fontWeight: location.pathname === '/admin' ? 600 : 400,
                      fontSize: isLaptop ? '0.85rem' : '0.875rem',
                      px: isLaptop ? 1 : 1.5,
                    }}
                  >
                    Admin
                  </Button>
                )}

                <Button
                  color="inherit"
                  startIcon={<MenuBookIcon />}
                  onClick={() => navigate('/documentation')}
                  sx={{
                    color: location.pathname === '/documentation' ? theme.palette.primary.main : 'inherit',
                    fontWeight: location.pathname === '/documentation' ? 600 : 400,
                    fontSize: isLaptop ? '0.85rem' : '0.875rem',
                    px: isLaptop ? 1 : 1.5,
                  }}
                >
                  Selectivate Guide
                </Button>
              </>
            )}
          </Box>

          {/* Center Section: Phase Navigation */}
          {!isSmallScreen && renderDesktopPhaseNav()}

          {/* Right Section: Email, Role, and Logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isLaptop ? 1 : 2, flexShrink: 0 }}>
            {user && (
              <>
                {/* Email and Role Display */}
                {!isSmallScreen && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {user.email && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 500,
                          fontSize: isLaptop ? '0.8rem' : '0.875rem',
                          maxWidth: isLaptop ? 150 : 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.email.split('@')[0]}
                      </Typography>
                    )}
                    {user.role && (
                      <Chip
                        label={user.role}
                        size="small"
                        color="primary"
                        sx={{
                          height: 18,
                          fontSize: isLaptop ? '0.65rem' : '0.7rem',
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* Selected Project Display */}
                {!isSmallScreen && selectedProject && (
                  <Chip
                    icon={<FolderOpen />}
                    label={selectedProject.name}
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      borderWidth: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(245, 130, 31, 0.1)',
                      },
                    }}
                    onClick={() => navigate('/settings')}
                    title="Click to change project in Settings"
                  />
                )}

                {/* Settings Button */}
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/settings')}
                  sx={{
                    color: location.pathname === '/settings' ? theme.palette.primary.main : 'inherit',
                  }}
                  title="Settings"
                >
                  <SettingsIcon />
                </IconButton>

                {/* Logout Button */}
                <Tooltip title="Logout" arrow placement="bottom">
                  <IconButton
                    onClick={handleLogout}
                    size="small"
                    sx={{
                      color: 'error.main',
                      border: '1px solid',
                      borderColor: 'error.main',
                      borderRadius: 1,
                      p: 0.75,
                      '&:hover': {
                        bgcolor: 'error.dark',
                        borderColor: 'error.dark',
                        color: 'white',
                      },
                    }}
                  >
                    <ExitToApp fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Phase progress pipeline — shown when a project is selected */}
      {selectedProject && (
        <PhaseProgressPipeline projectId={selectedProject.id} />
      )}

      {/* Mobile/Tablet Drawer */}
      {isSmallScreen && renderMobileNav()}
    </>
  );
};

export default NavigationBar;
